import type { JSX } from 'react';
import { useI18n } from '../../i18n';
import type { I18n } from '../../i18n';
import { isMixedContentBlocked } from '../../lib/probe';
import type { CheckResult } from '../../lib/types';
import { cx } from './cx';
import { Icon } from './Icon';
import { toneClass } from './tone';

/**
 * One row of the Results list — a port of
 * `android/.../ui/components/LinkTile.kt` (Material's three-slot `ListItem`).
 *
 * ### Open-in-browser is omitted, not forgotten
 *
 * Android's trailing slot holds two buttons: copy, and open in a browser. Only
 * copy survives here. Leaving the WebClip hands the foreground to Safari, and a
 * WebClip that loses the foreground is **suspended** — its timers stop and the
 * cycle in flight dies mid-run (SPEC §9). Android does not suffer that because
 * its checks run in a foreground service that outlives the Activity. A control
 * that silently ends the measurement the operator came here to read is worse
 * than no control, so `a11y_open_in_browser` is not among the ported strings.
 *
 * ### "Alive" always carries a question mark
 *
 * The reachable case renders `ios_status_alive_maybe`, a whole translated word
 * per locale ("Alive?", "Sống?", "正常？"), never `stat_alive` with a "?" glued
 * on in code. That is SPEC §13 made visible: a reachable probe proves
 * *something answered*, not that the real site answered — an ISP that blocks by
 * DNS-redirecting to a notice page produces a perfectly good response and this
 * app cannot tell the difference, because a `no-cors` fetch reports no status.
 * Dropping the mark launders a maybe into a fact at the exact point a person
 * reads it, and appending it here would hand CJK a Latin "?" where the language
 * writes "？". `stat_alive` stays unmarked and is still correct as a stat-cell
 * *label*, where it names a count rather than making a claim about one link.
 * `i18n.test.ts` holds both halves of that rule.
 */
export function LinkTile({
  result,
  onCopy,
}: {
  result: CheckResult;
  onCopy(url: string): void;
}): JSX.Element {
  const i18n = useI18n();
  const { t } = i18n;

  return (
    <div className="link-tile">
      {/* Three states, not Android's two — see `toneClass`, which is shared with
          the dashboard's compact row so the same link cannot read amber in one
          list and red in the other. */}
      <span className={cx('link-tile__dot', toneClass(result))} />
      <div className="link-tile__body">
        <div className="link-tile__url t-body-large clamp-1">{result.link.url}</div>
        <p className="link-tile__sub t-body-small break-anywhere">{subtitle(result, i18n)}</p>
      </div>
      <div className="link-tile__actions">
        {/*
          Copying, not opening. The clipboard write and the `result_copied`
          toast belong to the screen — a tile that owned them would need its own
          toast host per row.
        */}
        <button type="button" className="icon-btn" onClick={() => onCopy(result.link.url)}>
          <Icon name="content-copy" title={t('a11y_copy')} />
        </button>
      </div>
    </div>
  );
}

/**
 * The supporting line, built from parts and joined by `" · "`.
 *
 * Assembled piece by piece rather than from one format string, exactly as the
 * Kotlin does: the parts are optional, and a template with optional slots leaves
 * stray separators in every translation that nobody notices until a screenshot
 * comes back from the field.
 *
 * Two things this line never contains. An HTTP code — Android adds
 * `result_http_code` here and `statusCode` is always null on this platform, so
 * the key is not even ported. And the word "dead" for a plain `http://` domain:
 * Safari's mixed-content policy is a constant of the browser, not a property of
 * the link, and recording it as a verdict would put a permanent false negative
 * into the fleet data.
 */
function subtitle(result: CheckResult, { t, n }: I18n): string {
  const blocked = isMixedContentBlocked(result);
  const alive = result.healthStatus === 'healthy';
  const parts: string[] = [];

  if (blocked) parts.push(t('ios_status_unmeasurable'));
  else if (alive) parts.push(t('ios_status_alive_maybe'));
  else parts.push(t('stat_dead'));

  // Zero means nothing was measured — a plain-http link is rejected before any
  // fetch happens. Same reasoning as the `value_none` fallback in
  // CompactResultRow: "0 ms" would read as an impossibly fast site.
  if (result.responseMs > 0) parts.push(t('unit_ms', n(result.responseMs)));

  // The blocked case gets the translated explanation; everything else gets the
  // raw reason from the probe, which is a machine token ("timeout",
  // "network-error") and is deliberately not translated — it is the string that
  // was submitted to the backend, and an operator comparing the screen with the
  // dashboard has to see the same words.
  if (blocked) parts.push(t('ios_error_mixed_content'));
  else if (!alive && result.error !== undefined && result.error !== '') parts.push(result.error);

  return parts.join(' · ');
}
